import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Eye, Download, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import HelperWaiverCreateModal from './HelperWaiverCreateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

const HelperWaiverPanel = ({ user, language, onViewPdf, onDownloadPdf }) => {
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [emailWaiver, setEmailWaiver] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/helper-waivers`, { headers: authHeaders() });
      setWaivers(data);
    } catch (e) {
      toast.error('Erro ao carregar termos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fetchPdfBlob = async (id) => {
    const res = await fetch(`${API}/helper-waivers/${id}/view`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const viewPdf = async (w) => {
    try {
      const url = await fetchPdfBlob(w.id);
      onViewPdf(url, `Termo de Seguro - ${w.helper_name}`);
    } catch (e) {
      toast.error('Erro ao visualizar termo');
    }
  };

  const downloadPdf = async (w) => {
    try {
      const url = await fetchPdfBlob(w.id);
      onDownloadPdf(url, `termo_seguro_${w.helper_name.replace(/ /g, '_')}.pdf`);
    } catch (e) {
      toast.error('Erro ao baixar termo');
    }
  };

  const sendEmail = async () => {
    if (!emailTo.trim()) { toast.error('Informe o e-mail'); return; }
    try {
      setSending(true);
      await axios.post(`${API}/helper-waivers/${emailWaiver.id}/send-email`, { supervisor_email: emailTo.trim() }, { headers: authHeaders() });
      toast.success(`📧 Enviado para ${emailTo.trim()}`);
      setEmailWaiver(null);
      setEmailTo('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar e-mail');
    } finally {
      setSending(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Deletar o termo de ${w.helper_name}?`)) return;
    try {
      await axios.delete(`${API}/helper-waivers/${w.id}`, { headers: authHeaders() });
      toast.success('Termo removido');
      load();
    } catch (e) {
      toast.error('Erro ao deletar termo');
    }
  };

  const filtered = waivers.filter((w) =>
    !search.trim() ||
    w.helper_name?.toLowerCase().includes(search.toLowerCase()) ||
    w.pm_user?.toLowerCase().includes(search.toLowerCase())
  );

  const fmtDate = (d) => {
    if (!d) return '-';
    const [y, m, dd] = d.split('-');
    return `${m}/${dd}/${y}`;
  };

  return (
    <>
      <Card data-testid="helper-waiver-panel">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              🛡️ Termo de Seguro (Helper)
            </CardTitle>
            <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-waiver-btn">
              <Plus className="h-4 w-4 mr-2" /> Novo Termo
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Termo de ciência e isenção de responsabilidade — o helper declara que não possui Workers' Compensation e que o seguro não o cobre.
          </p>
          <div className="mt-3">
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-sm"
              data-testid="waiver-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="text-2xl mb-2">🔄</div>
              <div>Carregando termos...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <div className="text-gray-400 mb-4 text-4xl">🛡️</div>
              <h3 className="font-medium text-gray-700 mb-2">Nenhum termo encontrado</h3>
              <p className="text-gray-500 text-sm mb-4">Crie o termo de isenção de seguro para o helper assinar</p>
              <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="first-waiver-btn">
                <Plus className="h-4 w-4 mr-2" /> Criar Primeiro Termo
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((w) => (
                <div key={w.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`waiver-card-${w.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="text-lg">🛡️</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{w.helper_name}</div>
                        <div className="text-xs text-gray-500">Criado por {w.pm_user}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{fmtDate(w.date)}</div>
                      <Badge className="bg-green-100 text-green-800 text-xs mt-1">✍️ Assinado</Badge>
                    </div>
                  </div>
                  <div className="text-sm mb-3 flex justify-between">
                    <span className="text-gray-500">Subcontratado:</span>
                    <span className="font-medium truncate ml-2">{w.pm_name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <Button variant="outline" size="sm" onClick={() => viewPdf(w)} className="text-xs" data-testid={`waiver-view-${w.id}`}>
                      <Eye className="h-3 w-3 mr-1" /> Ver
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(w)} className="text-xs" data-testid={`waiver-download-${w.id}`}>
                      <Download className="h-3 w-3 mr-1" /> Baixar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEmailWaiver(w); setEmailTo(''); }} className="text-xs bg-blue-50 hover:bg-blue-100" data-testid={`waiver-email-${w.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Email
                    </Button>
                    {user?.role === 'admin' && (
                      <Button variant="outline" size="sm" onClick={() => remove(w)} className="text-xs text-red-600 hover:text-red-700" data-testid={`waiver-delete-${w.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Deletar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <HelperWaiverCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
        user={user}
        language={language}
      />

      {/* Modal de envio por e-mail */}
      <Dialog open={!!emailWaiver} onOpenChange={(v) => { if (!v) setEmailWaiver(null); }}>
        <DialogContent className="sm:max-w-md mx-2">
          <DialogHeader>
            <DialogTitle>📧 Enviar Termo por E-mail</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              Termo de <strong>{emailWaiver?.helper_name}</strong> será enviado em PDF anexo.
            </p>
            <Input
              type="email"
              placeholder="email@exemplo.com"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              data-testid="waiver-email-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setEmailWaiver(null)}>Cancelar</Button>
            <Button onClick={sendEmail} disabled={!emailTo.trim() || sending} className="bg-blue-600 hover:bg-blue-700" data-testid="waiver-send-email-btn">
              {sending ? '⏳ Enviando...' : '📧 Enviar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelperWaiverPanel;
